import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Building2,
  MapPin,
  RefreshCw,
  TrendingUp,
  Users,
  GraduationCap,
  BookOpen,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';

import { DonutChart, ComparisonBarChart } from '../../components/charts';
import { attendanceApi } from '../../services/api';
import { DateRangePicker } from '../../components/analytics';
import type { KVStudentAttendanceSummary, ClassSection, ClassAttendanceSummary } from '../../types/studentAnalytics';
import { MAX_ANALYTICS_DATE_RANGE_DAYS, clampToDate } from '../../../../shared/utils/dateUtils';

// Transform API response to component data structure
const transformApiResponse = (apiData: any): (KVStudentAttendanceSummary & { classes: ClassAttendanceSummary[] }) | null => {
  if (!apiData?.success || !apiData?.data || apiData.data.length === 0) {
    return null;
  }

  const schoolData = apiData.data[0];

  const resolveAttendancePercent = (section: any): number => {
    const rawValue = section.attendancePercentage ?? section.totalAttendancePercentage ?? section.attendancePercent;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
      if (section.totalEnrollment > 0) {
        return Math.round(((Number(section.totalPresent) || 0) / Number(section.totalEnrollment) * 100) * 10) / 10;
      }
      return 0;
    }
    return parsed;
  };

  const classes: ClassAttendanceSummary[] = schoolData.classes.map((cls: any) => {
    // For class 11 and 12, filter out sections with stream "NA" or "0"
    const classNum = parseInt(cls.class);
    const isHigherClass = classNum === 11 || classNum === 12;

    const filteredSections = cls.sections.filter((section: any) => {
      // For class 11/12, only show sections with valid streams (not NA or 0)
      if (isHigherClass) {
        return section.stream !== '0' && section.streamName !== 'NA';
      }
      return true;
    });

    const classSections: ClassSection[] = filteredSections.map((section: any) => ({
      className: cls.class,
      section: section.section,
      totalStudents: section.totalEnrollment || 0,
      presentCount: section.totalPresent || 0,
      absentCount: section.totalAbsent || 0,
      attendancePercent: resolveAttendancePercent(section),
      boysCount: section.totaboysEnrolled || 0,
      girlsCount: section.totalgirlsEnrolled || 0,
      boysPresent: section.totalBoysPresent || 0,
      girlsPresent: section.totalGirlsPresent || 0,
      // Add stream info for class 11/12
      stream: section.stream,
      streamName: section.streamName,
    }));

    const classTotal = classSections.reduce((sum, s) => sum + s.totalStudents, 0);
    const classPresent = classSections.reduce((sum, s) => sum + s.presentCount, 0);
    const classAbsent = classSections.reduce((sum, s) => sum + s.absentCount, 0);

    return {
      className: cls.class,
      sections: classSections,
      totalStudents: classTotal,
      presentCount: classPresent,
      absentCount: classAbsent,
      attendancePercent: classTotal > 0 ? Math.round((classPresent / classTotal) * 1000) / 10 : 0,
    };
  });

  // Calculate totals
  const totalStudents = classes.reduce((sum, cls) => sum + cls.totalStudents, 0);
  const presentCount = classes.reduce((sum, cls) => sum + cls.presentCount, 0);
  const absentCount = classes.reduce((sum, cls) => sum + cls.absentCount, 0);
  const boysCount = classes.reduce((sum, cls) =>
    sum + cls.sections.reduce((s, sec) => s + (sec.boysCount || 0), 0), 0);
  const girlsCount = classes.reduce((sum, cls) =>
    sum + cls.sections.reduce((s, sec) => s + (sec.girlsCount || 0), 0), 0);
  const boysPresent = classes.reduce((sum, cls) =>
    sum + cls.sections.reduce((s, sec) => s + (sec.boysPresent || 0), 0), 0);
  const girlsPresent = classes.reduce((sum, cls) =>
    sum + cls.sections.reduce((s, sec) => s + (sec.girlsPresent || 0), 0), 0);

  return {
    kvId: parseInt(schoolData.kvID),
    kvName: schoolData.schoolName || '',
    roId: 0,
    roName: schoolData.roName || '',
    stateName: schoolData.stateName || '',
    districtName: '',
    totalStudents,
    boysCount,
    girlsCount,
    presentCount,
    absentCount,
    noResponseCount: 0,
    attendancePercent: totalStudents > 0 ? Math.round((presentCount / totalStudents) * 1000) / 10 : 0,
    boysPresent,
    boysAbsent: boysCount - boysPresent,
    girlsPresent,
    girlsAbsent: girlsCount - girlsPresent,
    classes,
  };
};

const StudentKVAttendanceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { kvId } = useParams<{ kvId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const carouselImages = [img1];

  const today = new Date().toISOString().split('T')[0];
  const routeState = (location.state as any) || {};

  const initialFrom: string =
    routeState.fromDate || searchParams.get('fromDate') || (() => {
      const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0];
    })();
  const initialTo: string = clampToDate(
    initialFrom,
    routeState.toDate || searchParams.get('toDate') || today
  );

  // State
  const [fromDate, setFromDate] = useState<string>(initialFrom);
  const [toDate, setToDate] = useState<string>(initialTo);

  const [kvData, setKvData] = useState<(KVStudentAttendanceSummary & { classes: ClassAttendanceSummary[] }) | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());

  // Load data
  const loadData = useCallback(async () => {
    if (!kvId) return;

    setIsLoading(true);
    try {
      const response = await attendanceApi.getClassWiseSectionData(
        kvId,
        1,
        fromDate,
        toDate
      );

      const transformedData = transformApiResponse(response);
      setKvData(transformedData);
    } catch (error) {
      console.error('Error loading KV data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [kvId, fromDate, toDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle class expansion
  const toggleClassExpansion = (className: string) => {
    setExpandedClasses(prev => {
      const next = new Set(prev);
      if (next.has(className)) {
        next.delete(className);
      } else {
        next.add(className);
      }
      return next;
    });
  };

  // Attendance distribution data
  const attendanceDistribution = useMemo(() => {
    if (!kvData) return [];
    return [
      { name: 'Present', value: kvData.presentCount, color: '#10b981' },
      { name: 'Absent', value: kvData.absentCount, color: '#ef4444' },
    ];
  }, [kvData]);

  // Gender distribution data
  const genderDistribution = useMemo(() => {
    if (!kvData) return [];
    return [
      { name: 'Boys', value: kvData.boysCount, color: '#3b82f6' },
      { name: 'Girls', value: kvData.girlsCount, color: '#ec4899' },
    ];
  }, [kvData]);

  // Class attendance for bar chart
  const classAttendanceData = useMemo(() => {
    if (!kvData?.classes) return [];
    return kvData.classes.map((cls, idx) => ({
      id: idx + 1,
      name: `Class ${cls.className}`,
      value: cls.attendancePercent,
      rank: idx + 1,
      metadata: { totalStudents: cls.totalStudents }
    }));
  }, [kvData]);

  const getAttendanceColor = (percent: number) => {
    if (percent >= 90) return 'text-green-600 bg-green-50';
    if (percent >= 75) return 'text-blue-600 bg-blue-50';
    if (percent >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getAttendanceBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading school data...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!kvData) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <span className="text-gray-600">Data not Available in this date range</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 -pt-2 -sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <Building2 className="w-7 h-7" />
              {kvData.kvName}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {kvData.stateName}, {kvData.districtName}
              </span>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                {kvData.roName}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Preset buttons */}
              <div className="flex items-center gap-1.5">
                {[7, 14, 30].map(days => {
                  const f = fromDate ? new Date(fromDate + 'T12:00:00') : null;
                  const t = toDate ? new Date(toDate + 'T12:00:00') : null;
                  const diffDays = f && t ? Math.round(Math.abs(t.getTime() - f.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                  const isActive = 
                    days === 7 ? diffDays === 7 :
                    days === 14 ? diffDays === 14 :
                    days === 30 ? (diffDays >= 28 && diffDays <= 31) : false;
                  return (
                    <button
                      key={days}
                      type="button"
                      onClick={() => {
                        const baseToDate = toDate ? new Date(toDate + 'T12:00:00') : new Date();
                        const pastDate = new Date(baseToDate);
                        pastDate.setDate(baseToDate.getDate() - days);
                        
                        const formatDateStrLocal = (d: Date): string =>
                          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        
                        const newFromDate = formatDateStrLocal(pastDate);
                        const newToDate = formatDateStrLocal(baseToDate);
                        
                        setFromDate(newFromDate);
                        setToDate(newToDate);
                        
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('fromDate', newFromDate);
                        newParams.set('toDate', newToDate);
                        setSearchParams(newParams);
                      }}
                      className={`relative px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${isActive
                          ? 'bg-[#6A1786] text-white shadow-sm shadow-purple-200 scale-105'
                          : 'bg-white border border-gray-200 text-gray-500 hover:bg-purple-50 hover:text-purple-700 hover:scale-105'
                        }`}
                    >
                      {days === 7 ? '7D' : days === 14 ? '2W' : '1M'}
                      {isActive && (
                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active range display chip */}
              {fromDate && toDate && (
                <div className="hidden sm:flex items-center gap-1.5 bg-purple-50 border border-purple-100 rounded-lg px-2.5 py-1.5">
                  <span className="text-xs text-purple-500 font-medium">
                    {fromDate} → {toDate}
                  </span>
                </div>
              )}

              {/* Max days warning */}
              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap">
                <AlertTriangle className="w-3.5 h-3.5" />
                Max {MAX_ANALYTICS_DATE_RANGE_DAYS} days
              </span>

              {/* DateRangePicker */}
              <DateRangePicker
                value={{ fromDate, toDate }}
                onSubmit={(range) => {
                  if (range.fromDate && range.toDate) {
                    setFromDate(range.fromDate);
                    setToDate(range.toDate);
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('fromDate', range.fromDate);
                    newParams.set('toDate', range.toDate);
                    setSearchParams(newParams);
                  }
                }}
                disabled={isLoading}
              />
            </div>
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <GraduationCap className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{kvData.totalStudents.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {kvData.boysCount} Boys • {kvData.girlsCount} Girls
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Present Today</p>
                <p className="text-2xl font-bold text-green-600">{kvData.presentCount.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {kvData.boysPresent} Boys • {kvData.girlsPresent} Girls
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-100">
                <Users className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Absent Today</p>
                <p className="text-2xl font-bold text-red-600">{kvData.absentCount.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Attendance %</p>
                <p className="text-2xl font-bold text-amber-600">{kvData.attendancePercent}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Attendance Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Attendance</h3>
            <DonutChart
              data={attendanceDistribution}
              height={250}
              centerLabel="Total"
              centerValue={kvData.totalStudents.toLocaleString()}
              showLegend={true}
            />
          </div>

          {/* Gender Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
            <DonutChart
              data={genderDistribution}
              height={250}
              centerLabel="Students"
              centerValue={kvData.totalStudents.toLocaleString()}
              showLegend={true}
            />
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-blue-700">Boys Attendance</span>
                <span className="text-lg font-bold text-blue-700">
                  {kvData.boysCount > 0 ? Math.round((kvData.boysPresent! / kvData.boysCount) * 1000) / 10 : 0}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                <span className="text-sm font-medium text-pink-700">Girls Attendance</span>
                <span className="text-lg font-bold text-pink-700">
                  {kvData.girlsCount > 0 ? Math.round((kvData.girlsPresent! / kvData.girlsCount) * 1000) / 10 : 0}%
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Total Classes</span>
                <span className="text-lg font-bold text-gray-700">{kvData.classes?.length || 12}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Total Sections</span>
                <span className="text-lg font-bold text-gray-700">
                  {kvData.classes?.reduce((sum, cls) => sum + cls.sections.length, 0) || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Class-wise Attendance Chart */}
        {/* <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Class-wise Attendance</h3>
          <ComparisonBarChart
            data={classAttendanceData as any}
            height={350}
            horizontal={false}
            colorByPerformance={true}
          />
        </div> */}

        {/* Class-wise Detailed View */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Class-wise Attendance Details
            </h3>
          </div>

          <div className="divide-y divide-gray-100">
            {kvData.classes?.map((cls) => (
              <div key={cls.className}>
                {/* Class Header */}
                <button
                  onClick={() => toggleClassExpansion(cls.className)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-semibold text-gray-900 w-24">Class {cls.className}</span>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span>{cls.totalStudents} students</span>
                      <span className="text-green-600">{cls.presentCount} present</span>
                      <span className="text-red-600">{cls.absentCount} absent</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getAttendanceColor(cls.attendancePercent)}`}>
                      {cls.attendancePercent}%
                    </span>
                    {expandedClasses.has(cls.className) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Section Details */}
                {expandedClasses.has(cls.className) && (
                  <div className="px-6 pb-4 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {cls.sections.map((section) => {
                        const isHigherClass = parseInt(cls.className) === 11 || parseInt(cls.className) === 12;
                        const sectionWithStream = section as any;

                        return (
                          <div
                            key={`${cls.className}-${section.section}`}
                            onClick={() => {
                              const streamParam = isHigherClass && sectionWithStream.streamName && sectionWithStream.streamName !== 'NA'
                                ? `&stream=${encodeURIComponent(sectionWithStream.streamName)}`
                                : '';
                              navigate(`/attendance/student/analytics/kv/${kvId}/class/${cls.className}/section/${section.section}?kvName=${encodeURIComponent(kvData.kvName)}${streamParam}&fromDate=${fromDate}&toDate=${toDate}`);
                            }}
                            className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <span className="font-semibold text-gray-900">Section {section.section}</span>
                                {isHigherClass && sectionWithStream.streamName && sectionWithStream.streamName !== 'NA' && (
                                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${sectionWithStream.streamName === 'Science' ? 'bg-blue-100 text-blue-700' :
                                      sectionWithStream.streamName === 'Commerce' ? 'bg-green-100 text-green-700' :
                                        sectionWithStream.streamName === 'Humanities' ? 'bg-purple-100 text-purple-700' :
                                          'bg-gray-100 text-gray-700'
                                    }`}>
                                    {sectionWithStream.streamName}
                                  </span>
                                )}
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getAttendanceColor(section.attendancePercent)}`}>
                                {section.attendancePercent}%
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between text-gray-600">
                                <span>Total</span>
                                <span className="font-medium">{section.totalStudents}</span>
                              </div>
                              <div className="flex justify-between text-green-600">
                                <span>Present</span>
                                <span className="font-medium">{section.presentCount}</span>
                              </div>
                              <div className="flex justify-between text-red-600">
                                <span>Absent</span>
                                <span className="font-medium">{section.absentCount}</span>
                              </div>
                              <div className="pt-2 border-t border-gray-100">
                                <div className="flex justify-between text-gray-500">
                                  <span>Boys/Girls</span>
                                  <span>{section.boysCount}/{section.girlsCount}</span>
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getAttendanceBarColor(section.attendancePercent)}`}
                                style={{ width: `${section.attendancePercent}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentKVAttendanceDetail;
